import { TFunction } from 'i18next';
import { z } from 'zod';

import { qrcodeAPIService } from '~/api/qrcode';
import { getUrlZodValidation } from '~/utils/validation/url';

export const getGeneralBuilderSchema = ({
  t,
  prevQRCodeURLFromResponse,
}: {
  t: TFunction;
  prevQRCodeURLFromResponse?: string | null;
}) =>
  z.object({
    qrCodeName: z.string().default(''),
    qrCodeFolder: z.coerce.number().int().default(0),
    passwordProtection: z.object({
      isPasswordProtected: z.boolean().default(false),
      password: z.string().optional(),
    }),
    settings: z
      .object({
        isAutoGeneratedUrl: z.boolean().default(true),
        qrCodeUrl: z
          .string()
          .regex(/^[a-zA-Z0-9_-]+$/, { message: t('errors:url_2') })
          .default(''),
        isScanLimitsEnabled: z.boolean().default(false),
        scanLimit: z.coerce
          .number()
          .int()
          .min(1, { message: t('errors:scanMinNumber') })
          .positive()
          .optional(),
      })
      .superRefine(async (data, ctx) => {
        if (data.isScanLimitsEnabled) {
          if (typeof data.scanLimit === 'number') {
            if (data.scanLimit < 1) {
              ctx.addIssue({
                path: ['scanLimit'],
                code: z.ZodIssueCode.custom,
                message: t('errors:scanMinNumber'),
              });
            } else if (data.scanLimit > 10000000) {
              ctx.addIssue({
                path: ['scanLimit'],
                code: z.ZodIssueCode.custom,
                message: t('errors:scan_limit'),
              });
            }
          } else {
            ctx.addIssue({
              path: ['scanLimit'],
              code: z.ZodIssueCode.custom,
              message: t('inputs:required'),
            });
          }
        }

        if (!data.isAutoGeneratedUrl && data.qrCodeUrl !== prevQRCodeURLFromResponse) {
          try {
            await qrcodeAPIService.getUriValidation(data.qrCodeUrl);
          } catch (error) {
            if (error.message) {
              ctx.addIssue({
                path: ['qrCodeUrl'],
                code: z.ZodIssueCode.custom,
                message: error.message,
              });
            }
          }
        }
      }),
  });

export const getColorValidation = ({ t }: { t: TFunction }) =>
  z.string().regex(/^#([0-9A-F]{3}){1,2}$/i, { message: t('errors:invalidHexCode') });
const digitsAndOneDotRegex = /^\d+(\.\d+)?$/;

export const getBuilderLocationSchema = ({ t }: { t: TFunction }) =>
  z.object({
    complete: z
      .object({
        searchAddress: z.string().default('').optional(),
        street: z.string().default('').optional(),
        number: z.string().regex(/^\d*$/).default('').optional(),
        postalCode: z.string().regex(/^\d*$/).default('').optional(),
        city: z.string().default('').optional(),
        state: z.string().default('').optional(),
        country: z.string().default('').optional(),
      })
      .optional(),
    url: getUrlZodValidation({ isRequired: false, t }).optional(),
    coordinates: z
      .object({
        longitude: z
          .string()
          .refine((val) => {
            if (val) {
              return digitsAndOneDotRegex.test(val);
            }

            return true;
          })
          .optional(),
        latitude: z
          .string()
          .refine((val) => {
            if (val) {
              return digitsAndOneDotRegex.test(val);
            }

            return true;
          })
          .optional(),
      })
      .optional(),
  });

export const getGeneralStaticBuilderSchema = () =>
  z.object({
    name: z.string().optional(),
    qrCodeFolder: z.coerce.number().int().default(0),
    // passwordProtection: z.object({
    //   isPasswordProtected: z.boolean().default(false),
    //   password: z.string().optional(),
    // }),
  });
